import constants from '../../constants/Constants';

export type Product = {
  primary: string;
  secondary: string;
  /** One short line for a catalog row. */
  summary: string;
  to: string;
  category: string;
  /** Single leading icon for the card (optional). */
  icon?: string;
  twitterLink?: string;
  /** Lower = earlier in category lists. */
  priority?: number;
  /** Listed in Start Here, if FEATURED_ORDER has it. */
  featured?: boolean;
  /** The route stays for old links and analytics names, but the catalog omits it. */
  unavailable?: boolean;
};

export const matchesProductQuery = (product: Product, query: string) => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return (
    product.primary.toLowerCase().includes(normalized) ||
    product.secondary.toLowerCase().includes(normalized) ||
    product.summary.toLowerCase().includes(normalized) ||
    shelfWordsFor(product.to).includes(normalized) ||
    Boolean(product.icon && product.icon.includes(normalized))
  );
};

export const getSpotifyProducts = (): Product[] => [
  {
    icon: '🎟️',
    primary: 'Find Concerts',
    secondary: 'Find local concerts & events based on who you have saved across any number of Spotify playlists.',
    summary: 'Local shows from your playlists',
    to: '/events',
    twitterLink: 'https://x.com/1saarim/status/1711553636759957696',
    priority: 10,
    category: constants.TABS.SPOTIFY,
  },
  {
    icon: '🎧',
    primary: 'Discover New Music',
    secondary: 'Discover new, undiscovered music that is not already in your Spotify playlist.',
    summary: 'Songs not yet in your playlist',
    to: '/discover',
    twitterLink: 'https://x.com/1saarim/status/1713651311017885949',
    priority: 20,
    category: constants.TABS.SPOTIFY,
  },
  {
    icon: '🪩',
    primary: 'Create Set Playlist',
    secondary: 'Convert a tracklist or 1001tracklists.com set URL into a Spotify playlist.',
    summary: 'Turn a tracklist into a playlist',
    to: '/sets',
    twitterLink: 'https://x.com/1saarim/status/1722769873288462502',
    priority: 30,
    category: constants.TABS.SPOTIFY,
  },
  {
    icon: '🔄',
    primary: 'Find Duplicates',
    secondary: 'Detect and remove duplicate songs from your playlist.',
    summary: 'Find and remove duplicate songs',
    to: '/dupes',
    priority: 40,
    category: constants.TABS.SPOTIFY,
  },
  {
    icon: '🎸',
    primary: 'Count Artist Tracks',
    secondary: 'See how many songs per artist you have added, across any number of playlists.',
    summary: 'Songs per artist across playlists',
    to: '/artists',
    twitterLink: 'https://x.com/1saarim/status/1765476145179611401',
    priority: 50,
    category: constants.TABS.SPOTIFY,
  },
  {
    icon: '🗓️',
    primary: 'Fan Since',
    secondary: 'Find your earliest playlist save for each artist. Search, filter, and share your first finds.',
    summary: 'Your first save from each artist',
    to: '/fansince',
    priority: 55,
    category: constants.TABS.SPOTIFY,
  },
  {
    icon: '🎁',
    primary: 'My Wrapped',
    secondary: 'Explore twelve years of real listening history, or connect for your own top tracks, artists, and genres.',
    summary: 'Twelve years of listening history',
    to: '/wrapped',
    featured: true,
    priority: 56,
    category: constants.TABS.SPOTIFY,
  },
  {
    icon: '🎪',
    primary: 'Festival Playlist Filter',
    secondary: 'Filter your playlists by festival artists to discover songs you already know.',
    summary: 'Filter playlists by festival lineup',
    to: '/filter',
    twitterLink: 'https://x.com/1saarim/status/1865185623507898480',
    priority: 60,
    category: constants.TABS.SPOTIFY,
  },
  {
    icon: '🎛️',
    primary: 'Create DJ Sets Playlist',
    secondary: 'Create a playlist of recent tracks played live by your favorite artists.',
    summary: 'Recent live tracks by artist',
    to: '/djsets',
    twitterLink: 'https://x.com/1saarim/status/1905085309198721513',
    priority: 70,
    category: constants.TABS.SPOTIFY,
  },
  {
    icon: '🧬',
    primary: 'Playlist DNA',
    secondary: 'Analyze the genre breakdown, top artists, and diversity of any number of Spotify playlists.',
    summary: 'Genres, artists, and diversity',
    to: '/dna',
    priority: 80,
    category: constants.TABS.SPOTIFY,
  },
  {
    icon: '🤝',
    primary: 'Playlist Intersection',
    secondary: 'Find the songs that appear in every selected Spotify playlist.',
    summary: 'Songs in every playlist',
    to: '/overlap',
    priority: 90,
    category: constants.TABS.SPOTIFY,
  },
  {
    icon: '\u23F3',
    primary: 'Playlist Time Machine',
    secondary: 'Break any number of playlists into time windows — by year, quarter, season, or a rolling window.',
    summary: 'Split a playlist by time window',
    to: '/segments',
    priority: 100,
    category: constants.TABS.SPOTIFY,
  },
];

export const getProducts = (): Product[] => {
  const products: Product[] = [
    {
      icon: '🤖',
      primary: 'SaarimGPT',
      secondary: 'Chat with an AI version of me about my work, or have it use the site\'s tools for you.',
      summary: 'Chat with an AI version of me',
      to: '/chat',
      category: constants.TABS.AI,
      priority: 10,
    },
    {
      icon: '🗺️',
      primary: 'Crime Map',
      secondary: 'Explore reported crime in San Francisco and New York City. Drop a pin anywhere to rank the risk around it.',
      summary: 'Rank the risk around any pin',
      to: '/crime',
      featured: true,
      twitterLink: 'https://x.com/1saarim/status/1653646128888561664',
      category: constants.TABS.UTILITIES,
      priority: 20,
    },
    {
      icon: '📄',
      primary: 'Pastes',
      secondary: 'A Markdown editor with comments and shareable links.',
      summary: 'Markdown with comments and links',
      to: '/p',
      category: constants.TABS.UTILITIES,
      priority: 32,
    },
    {
      icon: '💰',
      primary: 'Bill Splitter',
      secondary: 'AI-powered receipt parsing for easy bill splitting and expense sharing.',
      summary: 'Parse a receipt and split it',
      to: '/split',
      twitterLink: 'https://x.com/1saarim/status/1647326385936175104',
      category: constants.TABS.UTILITIES,
      priority: 30,
    },
    {
      icon: '🚴',
      primary: 'Citi Bike Explorer',
      secondary: 'Every Citi Bike trip of the month on a map: busy stations, popular routes, busy streets, and the chance of a bike or a dock.',
      summary: 'Trips, routes, and live odds',
      to: '/citibike',
      category: constants.TABS.UTILITIES,
      priority: 22,
    },
    {
      icon: '🚆',
      primary: 'Departure Planner',
      secondary: 'When should I leave? Catch the next A/C subway from Columbus Circle or Penn off the live MTA feed.',
      summary: 'When to leave for the next train',
      to: '/mta',
      featured: true,
      category: constants.TABS.UTILITIES,
      priority: 25,
    },
    {
      icon: '❤️',
      primary: 'Health Dashboard',
      secondary: 'Trends in my VO2 max, sleep, heart rate, HRV, and workouts from Apple Health, updated daily.',
      summary: 'VO2 max, sleep, and heart rate',
      to: '/health',
      category: constants.TABS.UTILITIES,
      priority: 27,
    },
    {
      icon: '🔥',
      primary: 'FIRE Simulator',
      secondary: 'Monte Carlo retirement planning: 10,000 lives, taxes, housing, and the TC you need to stop working.',
      summary: '10,000 lives, taxes, and housing',
      to: '/fire',
      featured: true,
      category: constants.TABS.UTILITIES,
      priority: 28,
    },
    {
      icon: '📈',
      primary: 'RSU Strategy Simulator',
      secondary: 'Replay past windows and compare holding company stock with selling 25%, 50%, 75%, or all of each vest into SPY.',
      summary: 'Hold or sell each vest',
      to: '/rsu',
      category: constants.TABS.UTILITIES,
      priority: 29,
    },
    {
      icon: '✈️',
      primary: 'Flight Tracker',
      secondary: 'Log or import your flights and explore routes, miles, airlines, and travel history.',
      summary: 'Your routes, miles, and airlines',
      to: '/flights',
      category: constants.TABS.UTILITIES,
      priority: 26,
    },
    {
      icon: '🕵️',
      primary: 'Codenames',
      secondary: 'Play Codenames with friends on one shared link, with an AI spymaster to help you find clues.',
      summary: 'Play on one shared link',
      to: '/cn',
      category: constants.TABS.UTILITIES,
      priority: 35,
    },
    {
      icon: '🎞️',
      primary: 'Watchlist Match',
      secondary: 'Find films that appear across your friends\' public Letterboxd watchlists.',
      summary: 'Films on all your watchlists',
      to: '/letterboxd',
      category: constants.TABS.UTILITIES,
      priority: 37,
    },
    {
      icon: '🍿',
      primary: 'Discover Movies',
      secondary: 'Find highly rated films your Letterboxd friends have seen and you have not.',
      summary: 'Rated highly by friends',
      to: '/movies',
      category: constants.TABS.UTILITIES,
      priority: 38,
    },
    {
      icon: '🃏',
      primary: 'Poker Trainer',
      secondary: 'Learn to play Texas Hold\'em preflop optimally by betting position. Drill hands offline.',
      summary: 'Drill preflop by position',
      to: '/poker',
      featured: true,
      category: constants.TABS.UTILITIES,
      priority: 40,
    },
    {
      icon: '🎤',
      primary: 'Carti Translator',
      secondary: 'Convert anything to Cartinese.',
      summary: 'Convert anything to Cartinese',
      to: '/cartinese',
      featured: true,
      twitterLink: 'https://x.com/1saarim/status/1762366818612867571',
      category: constants.TABS.UTILITIES,
      priority: 50,
    },
    {
      icon: '📊',
      primary: 'Tweet Ranker',
      secondary: 'Find and sort tweets based on popularity for any number of Twitter handles.',
      summary: 'Rank tweets by popularity',
      to: '/twitter',
      twitterLink: 'https://x.com/1saarim/status/1639124725367709701',
      category: constants.TABS.UTILITIES,
      priority: 60,
      unavailable: true,
    },
    {
      icon: '📝',
      primary: 'AI Resume Builder',
      secondary: 'Create and edit your LaTeX resume in real time with AI.',
      summary: 'Edit a LaTeX resume with AI',
      to: '/resume',
      twitterLink: 'https://x.com/1saarim/status/1832922543000580404',
      category: constants.TABS.AI,
      priority: 20,
    },
    {
      icon: '🐦',
      primary: 'AI Twitter User Chat',
      secondary: "Use a ChatGPT-like interface to chat with someone's Twitter persona.",
      summary: 'Chat with a Twitter persona',
      to: '/tweetgpt',
      twitterLink: 'https://x.com/1saarim/status/1645154330289541121',
      category: constants.TABS.AI,
      priority: 30,
      unavailable: true,
    },
    {
      icon: '💡',
      primary: 'LLM Prompt Helper',
      secondary: 'Generate and improve prompts to give an LLM for a specific task.',
      summary: 'Generate and improve prompts',
      to: '/prompt',
      twitterLink: 'https://x.com/1saarim/status/1641911677711990785',
      category: constants.TABS.AI,
      priority: 40,
    },
    ...getSpotifyProducts(),
  ];

  return products.sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));
};

export const getCatalogProducts = (products: Product[] = getProducts()) =>
  products.filter((product) => !product.unavailable);

/** The Start Here row on a first visit. */
const FEATURED_ORDER = ['/crime', '/cartinese', '/wrapped', '/mta', '/poker', '/fire'];

export const getFeaturedProducts = (products: Product[] = getProducts()) =>
  FEATURED_ORDER.map((to) => products.find((p) => p.to === to && p.featured))
    .filter((p): p is Product => Boolean(p));

/**
 * The six shelves on Home and in the tools palette. `category` stays separate,
 * because analytics reports use it, and "Utilities" says nothing about a tool.
 */
const SHELVES = [
  {
    id: 'music',
    name: 'Music',
    paths: ['/events', '/wrapped', '/discover', '/sets', '/djsets', '/dupes', '/artists', '/fansince', '/filter', '/dna', '/overlap', '/segments'],
  },
  { id: 'chat-write', name: 'Chat & Write', paths: ['/chat', '/p', '/resume', '/prompt'] },
  { id: 'getting-around', name: 'Getting Around', paths: ['/citibike', '/mta', '/flights', '/crime'] },
  { id: 'money-health', name: 'Money & Health', paths: ['/fire', '/rsu', '/split', '/health'] },
  { id: 'games', name: 'Play', paths: ['/poker', '/cn', '/cartinese'] },
  { id: 'movies', name: 'Movies', paths: ['/letterboxd', '/movies'] },
];

/** The name and the address word, so "games" still finds the Play shelf at /?shelf=games. */
const shelfWordsFor = (to: string) => {
  const shelf = SHELVES.find(({ paths }) => paths.includes(to));
  return shelf ? `${shelf.name} ${shelf.id}`.toLowerCase() : '';
};

export type Shelf = { id: string; name: string; products: Product[] };

export const getShelves = (products: Product[] = getCatalogProducts()): Shelf[] =>
  SHELVES.map(({ id, name, paths }) => ({
    id,
    name,
    products: paths
      .map((to) => products.find((product) => product.to === to))
      .filter((product): product is Product => Boolean(product)),
  }));

/** Old links used ?category=. Those values land on the nearest shelf. */
const CATEGORY_SHELVES: Record<string, string> = { spotify: 'music', ai: 'chat-write' };

export const shelfForLink = (params: URLSearchParams) => {
  const id = params.get('shelf') ?? params.getAll('category').map((category) => CATEGORY_SHELVES[category]).find(Boolean);
  return getShelves().find((shelf) => shelf.id === id);
};
